// Thin fetch wrapper that detects expired / unauthorised sessions and
// resets client state. All callers should prefer this over raw `fetch`
// for backend calls so the 401 handling stays in one place.

import { API_BASE_PATH } from '../config';

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

// Guard against concurrent 401 responses each dispatching the event multiple
// times. A single notification is sufficient; subsequent ones are no-ops.
let _sessionExpiredDispatched = false;

/** @internal — exposed only for unit tests to reset between cases. */
export function _resetSessionExpiredGuard(): void {
  _sessionExpiredDispatched = false;
}

/** Resolve the absolute API base URL for the current host. */
export function apiBase(): string {
  return `${window.location.protocol}//${window.location.host}${API_BASE_PATH}`;
}

/**
 * fetch wrapper that throws SessionExpiredError on 401 and clears
 * sessionStorage so the next render returns the user to the login screen.
 * Automatically attaches a JWT Bearer token (stored as 'seatmapToken' in
 * sessionStorage) to every request that doesn't already have an Authorization
 * header.
 */
export async function apiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${apiBase()}${path.replace(/^\//, '')}`;

  // Inject JWT if present and not already supplied by the caller
  const token = sessionStorage.getItem('seatmapToken');
  const headers = new Headers(init?.headers as HeadersInit | undefined);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(url, { ...init, headers });
  if (res.status === 401) {
    sessionStorage.clear();
    // Notify the React tree via a CustomEvent so it can reset to the login
    // screen without a page reload. Reloading would abort any in-flight
    // mutations (e.g. the confirm-assignment POST). The guard prevents
    // duplicate events when concurrent requests all return 401 at once.
    if (!_sessionExpiredDispatched) {
      _sessionExpiredDispatched = true;
      window.dispatchEvent(new CustomEvent('session:expired'));
    }
    throw new SessionExpiredError();
  }
  return res;
}
